import React, { useRef } from 'react';
import { useSelector } from 'react-redux';

import { CoreLink, DynamicMediaItem } from '@uncinc/react-drupal-core';
import type { ImmutableEntity, Langcodes } from '@uncinc/react-drupal-core/types';
import { useWindowDimensions } from '@uncinc/uncinc-react-kitchen-sink';

import {
  SVGInline,
  WysiwygField,
} from '../../../../components';
import useBubble from '../../../../helpers/useBubble';
import { TaxonomyProjectType } from '../../../../types/uncinc_profile/taxonomy';
import { BLACK, WHITE, SCREEN_WIDE } from '../../../../config/constants';

import './style.scss';

interface TaxonomyProjectTypeTeaserProps {
  entity: ImmutableEntity<TaxonomyProjectType>;
}

const TaxonomyProjectTypeTeaser: React.FC<TaxonomyProjectTypeTeaserProps> = ({ entity }) => {
  const projectType = entity.toJS();
  const title = projectType.name[0].value;
  const body = projectType.description[0].value;
  const svg = projectType.field_svg[0];
  const mediaImg = projectType.field_media_item[0];

  const language = useSelector<Immutable.Map<any, any>>((state) => state.getIn(['ui', 'language']));
  const path = projectType.paths[language as Langcodes];

  const { width } = useWindowDimensions();
  const isNormal = width > SCREEN_WIDE;

  // Setting the svg path and rect in the same style object only recolors the path on hover
  // but not the rect inside the SVGs
  // Setting two separate style objects for these properties, does work.
  const svgPath = useRef<HTMLDivElement>(null);

  const slug = title.replace(/ /g, '-');

  const colorOnHover = (hover: boolean) => {
    if (svgPath.current && isNormal) {
      svgPath.current.textContent = `#icon-${slug} svg path {fill: #${hover ? BLACK : WHITE};}`;
    }
  };

  const {
    BubbleComponent,
    parentRef,
    onEnter,
    onLeave,
  } = useBubble();

  // @ts-ignore
  const enter = (event) => {
    onEnter(event);
    colorOnHover(true);
  };

  // @ts-ignore
  const leave = (event) => {
    onLeave(event);
    colorOnHover(false);
  };

  const svgColor = `#icon-${slug} svg path {fill: #${isNormal ? WHITE : BLACK};}`;

  return (
    <div className="TaxonomyProjectTypeTeaser col-12 col-md-6 col-lg-6">
      <CoreLink
        href={path}
        className="TaxonomyProjectTypeTeaser__link"
        aria-label={title}
      >
        <div className="TaxonomyProjectTypeTeaser__image">
          <DynamicMediaItem
            media={mediaImg}
            imageAspectRatio="1_1"
          />
          <div className="TaxonomyProjectTypeTeaser__gradient" />
        </div>

        <div
          className="TaxonomyProjectTypeTeaser__content"
          ref={parentRef}
          onMouseEnter={enter}
          onMouseLeave={leave}
        >
          {isNormal && (
            <BubbleComponent />
          )}
          <div className="TaxonomyProjectTypeTeaser__icon" id={`icon-${slug}`}>
            {/* @ts-ignore */}
            <style ref={svgPath}>
              {svgColor}
            </style>
            <SVGInline url={svg.source} />
          </div>
          <h2 className="TaxonomyProjectTypeTeaser__title">{title}</h2>
          <div className="TaxonomyProjectTypeTeaser__body">
            <WysiwygField
              html={body}
            />
          </div>
        </div>
      </CoreLink>
    </div>
  );
};

export default TaxonomyProjectTypeTeaser;
